import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Plus, Pencil, Trash2, X, Save, Image as ImageIcon, Tag, Package,
  Eye, EyeOff, Sparkles, Star, ShoppingBag, AlertCircle, Video, GripVertical,
  PlusCircle, Table as TableIcon, LayoutGrid, QrCode, Upload,
  Layers, Wand2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import IconPicker from "../../components/admin/IconPicker";
import RichEditor from "../../components/admin/RichEditor";
import { Icon } from "../../lib/icons";

const emptyIndicator = () => ({ icon: "Info", label: "", value: "", color: "" });

const emptyProduct = () => ({
  name: "",
  slug: "",
  short_description: "",
  long_description: "",
  category: "",
  featured_image: "",
  gallery: [],
  youtube_url: "",
  price_cents: 0,
  compare_at_price_cents: 0,
  cost_price_cents: 0,
  stock_tracking: true,
  stock_qty: 0,
  sku: "",
  ean13: "",
  weight_g: 0,
  vat_rate: -1,
  product_type: "simple",
  option_axes: [],
  variants: [],
  is_food: false,
  price_per_kg: false,
  unit_weight_g: null,
  allergens: [],
  ingredients: "",
  dlc_days: null,
  origin: "",
  conservation: "",
  is_rental: false,
  rental_unit: "day",
  rental_price_cents: 0,
  rental_min_duration: 1,
  rental_max_duration: 30,
  rental_deposit_cents: 0,
  rental_buffer_hours: 0,
  rental_checkin_time: "15:00",
  rental_checkout_time: "11:00",
  rental_require_phone: true,
  rental_description: "",
  rental_units: [],
  seo_title: "",
  seo_description: "",
  indicators: [],
  usage_tips: "",
  attributes: {},
  is_published: true,
  is_featured: false,
  sort_order: 0,
});

function priceFromCents(c) { return (c / 100).toFixed(2); }
function centsFromPrice(v) { return Math.round(parseFloat(v || 0) * 100) || 0; }

export default function ProductsAdmin() {
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [edit, setEdit] = useState(null);
  const [filter, setFilter] = useState("");
  const [shopEnabled, setShopEnabled] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("admin_products_view") || "table");

  useEffect(() => { localStorage.setItem("admin_products_view", viewMode); }, [viewMode]);

  const load = async () => {
    const [p, c, s] = await Promise.all([
      api.get("/products"),
      api.get("/product-categories/all"),
      api.get("/settings"),
    ]);
    setItems(p.data);
    setCategories(c.data);
    setShopEnabled(!!s.data.shop_enabled);
  };
  useEffect(() => { load(); }, []);

  const toggleShop = async (v) => {
    await api.put("/settings", { shop_enabled: v });
    setShopEnabled(v);
    toast.success(v ? "Boutique activée" : "Boutique désactivée");
  };

  const save = async () => {
    if (!edit.name?.trim()) { toast.error("Nom requis"); return; }
    try {
      if (edit.id) await api.put(`/products/${edit.id}`, edit);
      else await api.post("/products", edit);
      toast.success("Produit enregistré");
      setEdit(null); load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Supprimé"); load();
  };

  const togglePublish = async (p) => {
    await api.put(`/products/${p.id}`, { ...p, is_published: !p.is_published });
    load();
  };

  const filtered = items.filter(p => !filter || p.name.toLowerCase().includes(filter.toLowerCase()) || p.sku?.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="p-6 md:p-10" data-testid="admin-products-page">
      <header className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">E-commerce</div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Produits</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Catalogue boutique · fiches dynamiques · indicateurs personnalisables</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-[hsl(var(--muted))]/50 border" data-testid="shop-toggle-container">
            <ShoppingBag className={`w-4 h-4 ${shopEnabled ? "text-emerald-600" : "text-[hsl(var(--muted-foreground))]"}`} />
            <span className="text-xs font-bold uppercase tracking-wider">Boutique</span>
            <Switch checked={!!shopEnabled} onCheckedChange={toggleShop} data-testid="shop-enabled-toggle" />
          </div>
          <Button onClick={() => setEdit(emptyProduct())} className="bg-[color:var(--brand-red)]" data-testid="new-product-btn">
            <Plus className="w-4 h-4 mr-1" />Nouveau produit
          </Button>
        </div>
      </header>

      {!shopEnabled && (
        <Card className="p-4 border border-amber-200 bg-amber-50 mb-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            La boutique est actuellement <strong>désactivée</strong>. Les fiches produits ne sont pas visibles côté public tant que vous n'activez pas le toggle ci-dessus.
          </div>
        </Card>
      )}

      {/* Importer + view toggle */}
      <Card className="p-4 mb-4 border bg-gradient-to-br from-[color:var(--brand-navy)] via-[color:var(--brand-navy)] to-[color:var(--brand-sky)] text-white" data-testid="products-importer-banner">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
            <Upload className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/60 mb-0.5">Import catalogue</div>
            <div className="font-display font-bold text-sm">Importez depuis WooCommerce, Shopify ou un CSV</div>
            <div className="text-[11px] text-white/70 mt-0.5">Mapping automatique · dédoublonnage · gestion des images.</div>
          </div>
          <Link to="/admin/import-products">
            <Button className="bg-white hover:bg-white/90 text-[color:var(--brand-navy)] font-bold text-xs" data-testid="go-to-importer">
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Ouvrir l'import
            </Button>
          </Link>
        </div>
      </Card>

      {/* Filter + view mode */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <Input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Rechercher un produit ou un SKU..."
          className="max-w-sm"
          data-testid="products-filter"
        />
        <div className="ml-auto flex items-center gap-1 p-1 rounded-full border bg-[hsl(var(--muted))]/40" data-testid="products-view-toggle">
          <button
            type="button"
            onClick={() => setViewMode("table")}
            className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 h-8 rounded-full transition ${viewMode === "table" ? "bg-white shadow text-[color:var(--brand-navy)]" : "text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]"}`}
            data-testid="products-view-table"
          >
            <TableIcon className="w-3.5 h-3.5" /> Tableau
          </button>
          <button
            type="button"
            onClick={() => setViewMode("cards")}
            className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 h-8 rounded-full transition ${viewMode === "cards" ? "bg-white shadow text-[color:var(--brand-navy)]" : "text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]"}`}
            data-testid="products-view-cards"
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Cartes
          </button>
        </div>
      </div>

      {/* Empty state */}
      {!filtered.length && (
        <Card className="p-10 border text-center text-sm text-[hsl(var(--muted-foreground))]">
          <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
          Aucun produit. Cliquez sur <strong>Nouveau produit</strong> pour commencer votre catalogue, ou importez votre catalogue existant depuis WooCommerce/Shopify ci-dessus.
        </Card>
      )}

      {/* Table view (default) */}
      {viewMode === "table" && filtered.length > 0 && (
        <Card className="overflow-hidden border" data-testid="products-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--muted))]/50 text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                <tr>
                  <th className="text-left p-3 font-bold">Produit</th>
                  <th className="text-left p-3 font-bold hidden md:table-cell">SKU</th>
                  <th className="text-left p-3 font-bold hidden lg:table-cell">Catégorie</th>
                  <th className="text-right p-3 font-bold">Prix</th>
                  <th className="text-center p-3 font-bold hidden sm:table-cell">Stock</th>
                  <th className="text-center p-3 font-bold">État</th>
                  <th className="text-right p-3 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--border))]">
                {filtered.map(p => {
                  const cat = categories.find(c => c.slug === p.category || c.id === p.category);
                  const hasDiscount = p.compare_at_price_cents > p.price_cents;
                  return (
                    <tr key={p.id} className={`hover:bg-[hsl(var(--muted))]/30 ${!p.is_published ? "opacity-60" : ""}`} data-testid={`product-row-${p.slug}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-lg bg-[hsl(var(--muted))]/40 overflow-hidden flex items-center justify-center shrink-0">
                            {p.featured_image ? (
                              <img src={p.featured_image} alt="" className="w-full h-full object-cover" />
                            ) : <ImageIcon className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-[color:var(--brand-navy)] line-clamp-1 flex items-center gap-1.5">
                              {p.name}
                              {p.is_featured && <Star className="w-3 h-3 text-[color:var(--brand-red)]" fill="currentColor" />}
                            </div>
                            {p.short_description && <div className="text-[11px] text-[hsl(var(--muted-foreground))] line-clamp-1">{p.short_description}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="p-3 font-mono text-[11px] text-[hsl(var(--muted-foreground))] hidden md:table-cell">{p.sku || "—"}</td>
                      <td className="p-3 hidden lg:table-cell">
                        {cat ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[hsl(var(--muted))]/60">
                            <Tag className="w-2.5 h-2.5" />{cat.name}
                          </span>
                        ) : <span className="text-[11px] text-[hsl(var(--muted-foreground))]">—</span>}
                      </td>
                      <td className="p-3 text-right tabular-nums whitespace-nowrap">
                        {hasDiscount ? (
                          <>
                            <div className="font-bold text-[color:var(--brand-red)]">{priceFromCents(p.price_cents)}€</div>
                            <div className="text-[10px] line-through text-[hsl(var(--muted-foreground))]">{priceFromCents(p.compare_at_price_cents)}€</div>
                          </>
                        ) : (
                          <div className="font-bold text-[color:var(--brand-navy)]">{priceFromCents(p.price_cents)}€</div>
                        )}
                      </td>
                      <td className="p-3 text-center hidden sm:table-cell">
                        {p.stock_tracking ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            p.stock_qty === 0 ? "bg-red-100 text-red-700" :
                            p.stock_qty < 3 ? "bg-amber-100 text-amber-700" :
                            "bg-emerald-100 text-emerald-700"
                          }`}>{p.stock_qty}</span>
                        ) : <span className="text-[11px] text-[hsl(var(--muted-foreground))]">∞</span>}
                      </td>
                      <td className="p-3 text-center">
                        {p.is_published ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">
                            <Eye className="w-2.5 h-2.5" />Publié
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-200 text-slate-700">
                            <EyeOff className="w-2.5 h-2.5" />Brouillon
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1 justify-end">
                          <Link
                            to={`/admin/qr-labels?product=${encodeURIComponent(p.slug)}`}
                            className="w-8 h-8 rounded-md border hover:bg-[hsl(var(--muted))]/40 inline-flex items-center justify-center"
                            title="Étiquette QR code"
                            data-testid={`qr-product-${p.slug}`}
                          >
                            <QrCode className="w-3.5 h-3.5" />
                          </Link>
                          <button
                            type="button"
                            onClick={() => togglePublish(p)}
                            className="w-8 h-8 rounded-md border hover:bg-[hsl(var(--muted))]/40 inline-flex items-center justify-center"
                            title={p.is_published ? "Dépublier" : "Publier"}
                            data-testid={`toggle-publish-${p.slug}`}
                          >
                            {p.is_published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEdit(p)}
                            className="w-8 h-8 rounded-md border hover:bg-[hsl(var(--muted))]/40 inline-flex items-center justify-center"
                            title="Éditer"
                            data-testid={`edit-row-${p.slug}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => del(p.id)}
                            className="w-8 h-8 rounded-md border hover:bg-red-50 hover:border-red-300 text-red-600 inline-flex items-center justify-center"
                            title="Supprimer"
                            data-testid={`del-row-${p.slug}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Cards grid view (optional) */}
      {viewMode === "cards" && filtered.length > 0 && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map(p => (
          <Card key={p.id} className={`border overflow-hidden group ${!p.is_published ? "opacity-60" : ""}`}>
            <div className="aspect-square bg-[hsl(var(--muted))]/30 relative overflow-hidden">
              {p.featured_image ? (
                <img src={p.featured_image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[hsl(var(--muted-foreground))]">
                  <ImageIcon className="w-8 h-8" />
                </div>
              )}
              {p.is_featured && (
                <span className="absolute top-2 left-2 bg-[color:var(--brand-red)] text-white px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold flex items-center gap-1">
                  <Star className="w-2.5 h-2.5" fill="white"/>Mis en avant
                </span>
              )}
              {!p.is_published && (
                <span className="absolute top-2 right-2 bg-slate-600 text-white px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold">Brouillon</span>
              )}
              {p.stock_tracking && p.stock_qty === 0 && (
                <span className="absolute bottom-2 left-2 bg-red-600 text-white px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold">Rupture</span>
              )}
            </div>
            <div className="p-3">
              <div className="flex items-center gap-1 text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1">
                {p.sku && <span className="font-mono">{p.sku}</span>}
                {p.category && <><span>·</span><span>{p.category}</span></>}
              </div>
              <div className="font-display font-bold text-[color:var(--brand-navy)] line-clamp-1">{p.name}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-2 mt-0.5 min-h-[2.5em]">{p.short_description || "—"}</div>
              <div className="flex items-center justify-between mt-2">
                <div>
                  {p.compare_at_price_cents > p.price_cents ? (
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-bold text-[color:var(--brand-red)]">{priceFromCents(p.price_cents)}€</span>
                      <span className="text-[10px] line-through text-[hsl(var(--muted-foreground))]">{priceFromCents(p.compare_at_price_cents)}€</span>
                    </div>
                  ) : (
                    <span className="font-bold text-[color:var(--brand-navy)]">{priceFromCents(p.price_cents)}€</span>
                  )}
                </div>
                {p.stock_tracking && (
                  <span className={`text-[10px] uppercase tracking-wider font-bold ${p.stock_qty === 0 ? "text-red-600" : p.stock_qty < 3 ? "text-amber-600" : "text-emerald-600"}`}>
                    Stock: {p.stock_qty}
                  </span>
                )}
              </div>
              <div className="flex gap-1 mt-3">
                <Button variant="outline" size="sm" onClick={() => setEdit(p)} className="flex-1" data-testid={`edit-product-${p.slug}`}>
                  <Pencil className="w-3 h-3 mr-1" />Éditer
                </Button>
                <Link to={`/admin/qr-labels?product=${encodeURIComponent(p.slug)}`}>
                  <Button variant="outline" size="sm" title="QR code">
                    <QrCode className="w-3 h-3" />
                  </Button>
                </Link>
                <Button variant="outline" size="sm" onClick={() => togglePublish(p)} title={p.is_published ? "Dépublier" : "Publier"}>
                  {p.is_published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                </Button>
                <Button variant="outline" size="sm" onClick={() => del(p.id)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      )}

      {edit && (
        <ProductEditor
          edit={edit}
          setEdit={setEdit}
          categories={categories}
          onSave={save}
          onCancel={() => setEdit(null)}
          onReloadCategories={() => api.get("/product-categories/all").then(r => setCategories(r.data))}
        />
      )}
    </div>
  );
}

function ProductEditor({ edit, setEdit, categories, onSave, onCancel, onReloadCategories }) {
  const { settings } = useSettings();
  const [tab, setTab] = useState("general");
  const [newCat, setNewCat] = useState("");
  const updI = (i, key, v) => {
    const arr = [...(edit.indicators || [])];
    arr[i] = { ...arr[i], [key]: v };
    setEdit({ ...edit, indicators: arr });
  };
  const addI = () => setEdit({ ...edit, indicators: [...(edit.indicators || []), emptyIndicator()] });
  const delI = (i) => setEdit({ ...edit, indicators: edit.indicators.filter((_, x) => x !== i) });

  const addGalleryItem = () => {
    const url = window.prompt("URL de l'image (ex: /uploads/image.jpg ou https://...)");
    if (!url) return;
    setEdit({ ...edit, gallery: [...(edit.gallery || []), url.trim()] });
  };
  const delGalleryItem = (i) => setEdit({ ...edit, gallery: edit.gallery.filter((_, x) => x !== i) });

  const createCategory = async () => {
    if (!newCat.trim()) return;
    try {
      const { data } = await api.post("/product-categories", { name: newCat.trim() });
      await onReloadCategories();
      setEdit({ ...edit, category: data.slug });
      setNewCat("");
      toast.success("Catégorie créée");
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel} data-testid="product-editor-modal">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-[hsl(var(--muted-foreground))] font-semibold">{edit.id ? "Édition" : "Nouveau produit"}</div>
            <h2 className="font-display font-bold text-xl text-[color:var(--brand-navy)]">{edit.name || "Sans nom"}</h2>
          </div>
          <button onClick={onCancel}><X className="w-5 h-5" /></button>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b overflow-x-auto">
          <div className="flex gap-1">
            {[
              { id: "general", label: "Général" },
              { id: "media", label: "Médias" },
              { id: "pricing", label: "Prix & stock" },
              { id: "variants", label: "Variantes" },
              ...(settings?.rental_enabled ? [{ id: "rental", label: "Location" }] : []),
              ...(settings?.food_mode_enabled ? [{ id: "food", label: "Food" }] : []),
              { id: "indicators", label: "Indicateurs" },
              { id: "attributes", label: "Attributs" },
              { id: "seo", label: "SEO" },
            ].map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`px-3 py-2.5 text-xs uppercase tracking-wider font-semibold border-b-2 transition-colors whitespace-nowrap ${
                  tab === t.id ? "border-[color:var(--brand-sky)] text-[color:var(--brand-navy)]" : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]"
                }`}
                data-testid={`product-tab-${t.id}`}
              >{t.label}</button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {tab === "general" && (
            <>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Nom *</Label>
                  <Input value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} data-testid="product-name" />
                </div>
                <div>
                  <Label>Slug (URL)</Label>
                  <Input value={edit.slug} onChange={e => setEdit({ ...edit, slug: e.target.value })} placeholder="auto-généré" className="font-mono text-xs" />
                </div>
              </div>
              <div>
                <Label>Description courte</Label>
                <Input value={edit.short_description} onChange={e => setEdit({ ...edit, short_description: e.target.value })} placeholder="Une phrase d'accroche" />
              </div>
              <div>
                <Label>Description longue</Label>
                <RichEditor
                  value={edit.long_description || ""}
                  onChange={(html) => setEdit({ ...edit, long_description: html })}
                  placeholder="Décrivez votre produit en détail : caractéristiques, matériaux, utilisations, conseils…"
                  minHeight={260}
                />
                <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Éditeur enrichi : titres, listes, liens, images depuis la médiathèque. Le HTML est généré automatiquement.</p>
              </div>
              <div>
                <Label>Conseils d'utilisation / entretien</Label>
                <RichEditor
                  value={edit.usage_tips || ""}
                  onChange={(html) => setEdit({ ...edit, usage_tips: html })}
                  placeholder="Pour en prendre soin…"
                  minHeight={180}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-3 items-end">
                <div>
                  <Label>Catégorie</Label>
                  <Select value={edit.category} onValueChange={v => setEdit({ ...edit, category: v })}>
                    <SelectTrigger data-testid="product-category"><SelectValue placeholder="Sans catégorie" /></SelectTrigger>
                    <SelectContent>
                      {categories.map(c => <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="Nouvelle catégorie" className="h-10" />
                  <Button type="button" variant="outline" onClick={createCategory} disabled={!newCat.trim()}>+</Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 pt-2 border-t">
                <div className="flex items-center justify-between gap-2">
                  <Label>Publié</Label>
                  <Switch checked={edit.is_published} onCheckedChange={v => setEdit({ ...edit, is_published: v })} data-testid="product-published" />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <Label>Mis en avant</Label>
                  <Switch checked={edit.is_featured} onCheckedChange={v => setEdit({ ...edit, is_featured: v })} />
                </div>
                <div>
                  <Label>Ordre</Label>
                  <Input type="number" value={edit.sort_order} onChange={e => setEdit({ ...edit, sort_order: +e.target.value })} />
                </div>
              </div>

              {/* Type de produit : Simple ou Variable */}
              <div className="pt-3 border-t" data-testid="product-type-section">
                <Label className="text-xs font-bold">Type de produit</Label>
                <div className="mt-2 grid md:grid-cols-2 gap-2">
                  <label
                    onClick={() => setEdit({ ...edit, product_type: "simple" })}
                    className={`p-3 rounded-xl border-2 cursor-pointer transition text-xs ${(edit.product_type || "simple") === "simple" ? "border-[color:var(--brand-sky)] bg-sky-50/50" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid="product-type-simple"
                  >
                    <div className="font-bold text-[color:var(--brand-navy)]">Simple</div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">Un seul prix, un seul stock. Le plus courant.</div>
                  </label>
                  <label
                    onClick={() => setEdit({ ...edit, product_type: "variable" })}
                    className={`p-3 rounded-xl border-2 cursor-pointer transition text-xs ${edit.product_type === "variable" ? "border-[color:var(--brand-sky)] bg-sky-50/50" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid="product-type-variable"
                  >
                    <div className="font-bold text-[color:var(--brand-navy)]">Variable</div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">Déclinaisons (tailles, couleurs…) avec prix/stock/image par variante. Configurez les variantes dans l'onglet « Variantes ».</div>
                  </label>
                </div>
              </div>
            </>
          )}

          {tab === "media" && (
            <>
              <div>
                <Label>Image principale (URL)</Label>
                <Input value={edit.featured_image} onChange={e => setEdit({ ...edit, featured_image: e.target.value })} placeholder="/uploads/monstera.jpg" />
                {edit.featured_image && (
                  <img src={edit.featured_image} alt="" className="mt-2 h-32 w-32 object-cover rounded-lg border" />
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Galerie photos</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addGalleryItem}>
                    <PlusCircle className="w-3 h-3 mr-1" />Ajouter
                  </Button>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {(edit.gallery || []).map((g, i) => (
                    <div key={i} className="relative group">
                      <img src={g} alt="" className="h-24 w-full object-cover rounded-lg border" />
                      <button
                        type="button"
                        onClick={() => delGalleryItem(i)}
                        className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      ><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                  {!edit.gallery?.length && (
                    <div className="col-span-4 text-center text-xs text-[hsl(var(--muted-foreground))] py-4 border-2 border-dashed rounded-lg">
                      Aucune image additionnelle
                    </div>
                  )}
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-1.5"><Video className="w-3.5 h-3.5" />Vidéo YouTube</Label>
                <Input value={edit.youtube_url} onChange={e => setEdit({ ...edit, youtube_url: e.target.value })} placeholder="https://youtube.com/watch?v=XXXXXXX" />
              </div>
            </>
          )}

          {tab === "pricing" && (
            <>
              <div className="grid md:grid-cols-3 gap-3" data-testid="product-pricing-row">
                <div>
                  <Label>Prix de vente (€) <span className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase ml-1">TTC public</span></Label>
                  <Input type="number" step="0.01" value={priceFromCents(edit.price_cents)} onChange={e => setEdit({ ...edit, price_cents: centsFromPrice(e.target.value) })} data-testid="product-price" />
                </div>
                <div>
                  <Label>Prix barré / promo (€)</Label>
                  <Input type="number" step="0.01" value={priceFromCents(edit.compare_at_price_cents)} onChange={e => setEdit({ ...edit, compare_at_price_cents: centsFromPrice(e.target.value) })} />
                </div>
                <div>
                  <Label className="flex items-center gap-1 text-[color:var(--brand-navy)]">
                    <span className="inline-flex w-4 h-4 rounded bg-amber-400/20 text-amber-700 text-[9px] items-center justify-center font-black">€</span>
                    Prix d'achat (€)
                    <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wider bg-amber-100 px-1.5 py-0.5 rounded">Admin only</span>
                  </Label>
                  <Input type="number" step="0.01" value={priceFromCents(edit.cost_price_cents || 0)} onChange={e => setEdit({ ...edit, cost_price_cents: centsFromPrice(e.target.value) })} data-testid="product-cost-price" placeholder="COGS — jamais affiché au public" />
                </div>
              </div>
              {/* Margin recap */}
              {edit.price_cents > 0 && (() => {
                const price = edit.price_cents || 0;
                const cost = edit.cost_price_cents || 0;
                const margin = price - cost;
                const rate = price ? (margin / price) : 0;
                const markup = cost ? (margin / cost) : 0;
                const marginClr = rate >= 0.4 ? "text-emerald-600" : rate >= 0.2 ? "text-amber-600" : "text-red-600";
                return (
                  <div className="p-3 rounded-xl bg-[hsl(var(--muted))]/30 grid grid-cols-3 gap-3" data-testid="product-margin-recap">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">Marge unitaire</div>
                      <div className={`font-display text-xl font-black ${marginClr} tabular-nums`}>{priceFromCents(margin)} €</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">Taux de marge</div>
                      <div className={`font-display text-xl font-black ${marginClr} tabular-nums`}>{(rate * 100).toFixed(1)} %</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">Coefficient</div>
                      <div className="font-display text-xl font-black text-[color:var(--brand-navy)] tabular-nums">×{(1 + markup).toFixed(2)}</div>
                    </div>
                  </div>
                );
              })()}
              <div className="flex items-center gap-3 pt-2 border-t">
                <Switch checked={edit.stock_tracking} onCheckedChange={v => setEdit({ ...edit, stock_tracking: v })} />
                <Label>Suivre le stock</Label>
              </div>
              {edit.stock_tracking && (
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <Label>Quantité</Label>
                    <Input type="number" value={edit.stock_qty} onChange={e => setEdit({ ...edit, stock_qty: +e.target.value })} data-testid="product-stock" />
                  </div>
                  <div>
                    <Label>SKU</Label>
                    <Input value={edit.sku} onChange={e => setEdit({ ...edit, sku: e.target.value })} className="font-mono" />
                  </div>
                  <div>
                    <Label>EAN-13 (code-barres)</Label>
                    <Input value={edit.ean13} onChange={e => setEdit({ ...edit, ean13: e.target.value })} className="font-mono" />
                  </div>
                </div>
              )}
              <div>
                <Label>Poids (grammes)</Label>
                <Input type="number" value={edit.weight_g} onChange={e => setEdit({ ...edit, weight_g: +e.target.value })} className="max-w-xs" />
              </div>

              {/* Taux de TVA spécifique au produit */}
              {settings?.vat_applicable !== false && (
                <div className="pt-3 border-t" data-testid="product-vat-section">
                  <Label className="flex items-center gap-1 text-[color:var(--brand-navy)]">
                    <span className="inline-flex w-4 h-4 rounded bg-sky-400/20 text-sky-700 text-[9px] items-center justify-center font-black">%</span>
                    Taux de TVA pour ce produit
                  </Label>
                  <Select
                    value={String(edit.vat_rate ?? -1)}
                    onValueChange={v => setEdit({ ...edit, vat_rate: parseFloat(v) })}
                  >
                    <SelectTrigger className="mt-1.5 max-w-md" data-testid="product-vat-rate">
                      <SelectValue placeholder="Taux par défaut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="-1">Taux par défaut ({settings?.shop_tax_rate ?? 20} %)</SelectItem>
                      {(settings?.invoice_vat_rates || []).map((r, i) => (
                        <SelectItem key={i} value={String(r.rate)}>{r.label || `${r.rate} %`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5">
                    Laissez « Taux par défaut » pour appliquer le taux global (configuré dans <Link to="/admin/billing" className="underline text-[color:var(--brand-sky)]">Facturation &amp; TVA</Link>).
                  </div>
                </div>
              )}
            </>
          )}

          {tab === "variants" && (
            <VariantsEditor edit={edit} setEdit={setEdit} />
          )}

          {tab === "rental" && (
            <RentalEditor edit={edit} setEdit={setEdit} />
          )}

          {tab === "food" && (
            <FoodEditor edit={edit} setEdit={setEdit} />
          )}

          {tab === "indicators" && (
            <>
              <div className="text-sm text-[hsl(var(--muted-foreground))]">
                Indicateurs personnalisables (ensoleillement, arrosage, niveau de difficulté, période de plantation, matière, etc.). Chaque indicateur possède une icône, un label et une valeur.
              </div>
              <div className="space-y-2" data-testid="product-indicators">
                {(edit.indicators || []).map((ind, i) => (
                  <Card key={i} className="p-3 border flex items-start gap-3">
                    <GripVertical className="w-4 h-4 text-[hsl(var(--muted-foreground))] mt-2 shrink-0" />
                    <IconPicker value={ind.icon} onChange={v => updI(i, "icon", v)} />
                    <div className="grid grid-cols-2 gap-2 flex-1">
                      <Input value={ind.label} onChange={e => updI(i, "label", e.target.value)} placeholder="Label (ex: Ensoleillement)" />
                      <Input value={ind.value} onChange={e => updI(i, "value", e.target.value)} placeholder="Valeur (ex: Plein soleil)" />
                    </div>
                    <Button variant="outline" size="sm" onClick={() => delI(i)}><Trash2 className="w-3 h-3" /></Button>
                  </Card>
                ))}
                <Button type="button" variant="outline" onClick={addI} className="w-full" data-testid="add-indicator-btn">
                  <Plus className="w-4 h-4 mr-1" />Ajouter un indicateur
                </Button>
              </div>
              {(edit.indicators || []).length > 0 && (
                <div className="mt-4 p-4 rounded-lg bg-[hsl(var(--muted))]/30 border">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] mb-2">Preview</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {edit.indicators.map((ind, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white border">
                        <Icon name={ind.icon || "Info"} className="w-4 h-4 text-[color:var(--brand-sky)]" />
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] truncate">{ind.label || "—"}</div>
                          <div className="text-sm font-semibold text-[color:var(--brand-navy)] truncate">{ind.value || "—"}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {tab === "attributes" && (
            <div className="space-y-3" data-testid="product-attributes-editor">
              <div className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
                Ajoutez des attributs libres (clé / valeur) qui apparaîtront automatiquement en tant que <strong>filtres dans la boutique</strong>.
                Exemples : <em>Couleur = Rouge</em>, <em>Taille = M</em>, <em>Matière = Coton bio</em>, <em>Marque = Daikin</em>.
              </div>
              <div className="space-y-2">
                {Object.entries(edit.attributes || {}).map(([k, v], idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`attr-row-${idx}`}>
                    <Input
                      value={k}
                      onChange={e => {
                        const newKey = e.target.value;
                        const entries = Object.entries(edit.attributes || {});
                        entries[idx] = [newKey, v];
                        setEdit({ ...edit, attributes: Object.fromEntries(entries.filter(([nk]) => nk !== "" || entries.indexOf([nk, v]) === idx)) });
                      }}
                      placeholder="Nom (ex: Couleur)"
                      className="flex-1 text-sm"
                      data-testid={`attr-key-${idx}`}
                    />
                    <span className="text-[hsl(var(--muted-foreground))] text-xs">=</span>
                    <Input
                      value={v}
                      onChange={e => setEdit({ ...edit, attributes: { ...edit.attributes, [k]: e.target.value } })}
                      placeholder="Valeur (ex: Rouge)"
                      className="flex-1 text-sm"
                      data-testid={`attr-value-${idx}`}
                    />
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => {
                        const copy = { ...edit.attributes };
                        delete copy[k];
                        setEdit({ ...edit, attributes: copy });
                      }}
                      className="text-red-600 shrink-0"
                      data-testid={`attr-del-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => {
                  // Trouve une clé libre
                  let k = "Nouvel attribut";
                  let i = 1;
                  while (edit.attributes && edit.attributes[k]) { i++; k = `Nouvel attribut ${i}`; }
                  setEdit({ ...edit, attributes: { ...(edit.attributes || {}), [k]: "" } });
                }}
                data-testid="add-attr"
              >
                <Plus className="w-3 h-3 mr-1.5" /> Ajouter un attribut
              </Button>
            </div>
          )}

          {tab === "seo" && (
            <>
              <div>
                <Label>SEO Title <span className="text-[10px] text-[hsl(var(--muted-foreground))]">(60 car. max)</span></Label>
                <Input value={edit.seo_title} onChange={e => setEdit({ ...edit, seo_title: e.target.value.slice(0, 80) })} placeholder={edit.name} />
                <div className="text-[10px] text-right font-mono text-[hsl(var(--muted-foreground))] mt-1">{edit.seo_title?.length || 0}/60</div>
              </div>
              <div>
                <Label>SEO Description <span className="text-[10px] text-[hsl(var(--muted-foreground))]">(160 car. max)</span></Label>
                <Textarea rows={3} value={edit.seo_description} onChange={e => setEdit({ ...edit, seo_description: e.target.value.slice(0, 200) })} placeholder={edit.short_description} />
                <div className="text-[10px] text-right font-mono text-[hsl(var(--muted-foreground))] mt-1">{edit.seo_description?.length || 0}/160</div>
              </div>
              <div className="p-4 rounded-lg bg-[hsl(var(--muted))]/30 border">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] mb-2">Preview Google</div>
                <div className="space-y-1">
                  <div className="text-sm text-emerald-700">votre-site.fr › shop › {edit.slug || "produit"}</div>
                  <div className="text-lg text-blue-700 hover:underline cursor-pointer line-clamp-1">{edit.seo_title || edit.name || "Nom du produit"}</div>
                  <div className="text-sm text-slate-700 line-clamp-2">{edit.seo_description || edit.short_description || "Description..."}</div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex justify-between items-center gap-2">
          <div className="text-xs text-[hsl(var(--muted-foreground))]">
            {edit.id ? "Modifications non enregistrées" : "Nouveau brouillon"}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>Annuler</Button>
            <Button onClick={onSave} className="bg-[color:var(--brand-navy)]" data-testid="save-product-btn">
              <Save className="w-4 h-4 mr-1.5" />Enregistrer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// VariantsEditor — Produits variables (tailles × couleurs × etc.)
// ============================================================================
function VariantsEditor({ edit, setEdit }) {
  const isVariable = edit.product_type === "variable";
  const axes = edit.option_axes || [];
  const variants = edit.variants || [];

  const setAxis = (i, field, value) => {
    const next = axes.map((a, idx) => idx === i ? { ...a, [field]: value } : a);
    setEdit({ ...edit, option_axes: next });
  };
  const setAxisValues = (i, raw) => {
    const values = raw.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean);
    setAxis(i, "values", values);
  };
  const addAxis = () => setEdit({ ...edit, option_axes: [...axes, { name: "", values: [] }] });
  const delAxis = (i) => setEdit({ ...edit, option_axes: axes.filter((_, idx) => idx !== i) });

  const updVariant = (vi, field, value) => {
    const next = variants.map((v, i) => i === vi ? { ...v, [field]: value } : v);
    setEdit({ ...edit, variants: next });
  };
  const delVariant = (vi) => setEdit({ ...edit, variants: variants.filter((_, i) => i !== vi) });
  const setDefault = (vi) => {
    const next = variants.map((v, i) => ({ ...v, is_default: i === vi }));
    setEdit({ ...edit, variants: next });
  };

  const generate = async (keepExisting) => {
    if (!edit.id) { toast.error("Enregistrez d'abord le produit, puis générez les variantes."); return; }
    const validAxes = axes.filter(a => (a.name || "").trim() && (a.values || []).length > 0);
    if (validAxes.length === 0) { toast.error("Ajoutez au moins un axe avec des valeurs."); return; }
    try {
      const r = await api.post(`/products/${edit.id}/variants/generate`, {
        option_axes: validAxes,
        base_sku: edit.sku || "",
        keep_existing: !!keepExisting,
      });
      setEdit({ ...edit, variants: r.data.variants, product_type: "variable", option_axes: validAxes });
      toast.success(`${r.data.count} variante(s) générée(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur génération");
    }
  };

  const pickImage = (vi) => {
    const url = window.prompt("URL de l'image de la variante (ou laisser vide pour utiliser l'image principale)");
    if (url !== null) updVariant(vi, "image", url.trim());
  };

  if (!isVariable) {
    return (
      <div className="p-6 rounded-xl border-2 border-dashed bg-slate-50 text-center">
        <Layers className="w-8 h-8 mx-auto text-slate-400" />
        <p className="mt-3 text-sm text-[color:var(--brand-navy)] font-semibold">Produit simple (un seul prix / stock)</p>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 max-w-md mx-auto">Pour gérer des variantes (tailles, couleurs, etc.), passez ce produit en <strong>« Variable »</strong> dans l'onglet <em>Général → Type de produit</em>.</p>
        <Button className="mt-3 text-xs" variant="outline" size="sm" onClick={() => setEdit({ ...edit, product_type: "variable" })} data-testid="switch-to-variable-btn">
          Passer en produit variable
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="product-variants-editor">
      {/* Axes */}
      <div className="p-4 rounded-xl border bg-slate-50/40 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs font-bold text-[color:var(--brand-navy)] flex items-center gap-1">
            <Tag className="w-3.5 h-3.5" /> Axes de déclinaison
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addAxis} className="text-xs" data-testid="add-axis-btn">
            <Plus className="w-3 h-3 mr-1" /> Ajouter un axe
          </Button>
        </div>
        {axes.length === 0 && (
          <p className="text-xs text-[hsl(var(--muted-foreground))] italic">Ajoutez au moins un axe (ex: Taille, Couleur) avec ses valeurs séparées par virgules (ex: S, M, L).</p>
        )}
        <div className="space-y-2">
          {axes.map((a, i) => (
            <div key={i} className="grid md:grid-cols-[180px_1fr_auto] gap-2 items-start p-2 rounded-lg bg-white border" data-testid={`axis-row-${i}`}>
              <Input
                value={a.name}
                onChange={e => setAxis(i, "name", e.target.value)}
                placeholder="Taille"
                className="text-sm"
                data-testid={`axis-name-${i}`}
              />
              <Textarea
                rows={1}
                value={(a.values || []).join(", ")}
                onChange={e => setAxisValues(i, e.target.value)}
                placeholder="S, M, L, XL"
                className="text-sm font-mono"
                data-testid={`axis-values-${i}`}
              />
              <Button variant="outline" size="sm" onClick={() => delAxis(i)} className="text-red-600 h-10">✕</Button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t">
          <Button onClick={() => generate(false)} className="bg-[color:var(--brand-sky)] text-xs" data-testid="generate-variants-btn">
            <Wand2 className="w-3.5 h-3.5 mr-1" /> Générer les variantes
          </Button>
          {variants.length > 0 && (
            <Button onClick={() => generate(true)} variant="outline" className="text-xs" data-testid="regenerate-merge-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Régénérer (fusion : conserve les prix/stocks saisis)
            </Button>
          )}
          {!edit.id && <span className="text-[11px] text-amber-700 italic">⚠️ Enregistrez d'abord le produit.</span>}
        </div>
      </div>

      {/* Variants Table */}
      {variants.length > 0 && (
        <div className="rounded-xl border overflow-hidden" data-testid="variants-table">
          <div className="px-4 py-2.5 bg-slate-50 flex items-center justify-between">
            <div className="text-xs font-bold text-[color:var(--brand-navy)]">
              {variants.length} variante(s) — éditables individuellement
            </div>
            <div className="text-[10px] text-[hsl(var(--muted-foreground))]">
              💡 Cliquez sur l'étoile pour définir la variante par défaut (pré-sélectionnée sur la fiche produit)
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50/70 text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                <tr>
                  <th className="px-2 py-2 text-left w-10"></th>
                  <th className="px-2 py-2 text-left w-14">Image</th>
                  <th className="px-2 py-2 text-left">Variante</th>
                  <th className="px-2 py-2 text-left w-36">SKU</th>
                  <th className="px-2 py-2 text-right w-28">Prix TTC (€)</th>
                  <th className="px-2 py-2 text-right w-28">Prix barré (€)</th>
                  <th className="px-2 py-2 text-right w-24">Stock</th>
                  <th className="px-2 py-2 text-center w-16">Actif</th>
                  <th className="px-2 py-2 text-center w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {variants.map((v, i) => {
                  const label = Object.values(v.options || {}).join(" · ");
                  return (
                    <tr key={v.id || i} className="hover:bg-slate-50/50" data-testid={`variant-row-${i}`}>
                      <td className="px-2 py-1.5 text-center">
                        <button
                          onClick={() => setDefault(i)}
                          className={`p-1 rounded ${v.is_default ? "text-amber-500" : "text-slate-300 hover:text-amber-400"}`}
                          title={v.is_default ? "Variante par défaut" : "Définir par défaut"}
                          data-testid={`variant-default-${i}`}
                        >
                          <Star className="w-3.5 h-3.5" fill={v.is_default ? "currentColor" : "none"} />
                        </button>
                      </td>
                      <td className="px-2 py-1.5">
                        <button
                          type="button"
                          onClick={() => pickImage(i)}
                          className="w-10 h-10 rounded border bg-slate-50 overflow-hidden hover:ring-2 hover:ring-[color:var(--brand-sky)]"
                          data-testid={`variant-image-${i}`}
                        >
                          {v.image ? <img src={v.image} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-slate-400 mx-auto" />}
                        </button>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="font-semibold text-[color:var(--brand-navy)] text-xs">{label || "—"}</div>
                        {v.is_default && <span className="text-[9px] text-amber-700 font-bold uppercase tracking-wider">Par défaut</span>}
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          value={v.sku || ""}
                          onChange={e => updVariant(i, "sku", e.target.value)}
                          className="h-8 text-xs font-mono"
                          data-testid={`variant-sku-${i}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="number" step="0.01"
                          value={(v.price_cents || 0) / 100}
                          onChange={e => updVariant(i, "price_cents", Math.round((parseFloat(e.target.value) || 0) * 100))}
                          className="h-8 text-xs text-right tabular-nums"
                          data-testid={`variant-price-${i}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="number" step="0.01"
                          value={(v.compare_at_price_cents || 0) / 100}
                          onChange={e => updVariant(i, "compare_at_price_cents", Math.round((parseFloat(e.target.value) || 0) * 100))}
                          className="h-8 text-xs text-right tabular-nums"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="number"
                          value={v.stock_qty || 0}
                          onChange={e => updVariant(i, "stock_qty", parseInt(e.target.value) || 0)}
                          className="h-8 text-xs text-right tabular-nums"
                          data-testid={`variant-stock-${i}`}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Switch checked={v.is_active !== false} onCheckedChange={val => updVariant(i, "is_active", val)} />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button
                          onClick={() => { if (window.confirm("Supprimer cette variante ?")) delVariant(i); }}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Supprimer"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// FoodEditor — Spécificités Food (allergènes, ingrédients, DLC, origine, prix/kg)
// ============================================================================
function FoodEditor({ edit, setEdit }) {
  const [allergensRef, setAllergensRef] = useState([]);
  useEffect(() => {
    api.get("/shop/meta/allergens").then(r => setAllergensRef(r.data || [])).catch(() => {});
  }, []);

  const toggleAllergen = (code) => {
    const list = edit.allergens || [];
    const next = list.includes(code) ? list.filter(c => c !== code) : [...list, code];
    setEdit({ ...edit, allergens: next });
  };

  return (
    <div className="space-y-5" data-testid="product-food-editor">
      <label className="flex items-start gap-3 p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50/30 cursor-pointer">
        <Switch checked={!!edit.is_food} onCheckedChange={v => setEdit({ ...edit, is_food: v })} data-testid="product-is-food" />
        <div>
          <div className="text-sm font-bold text-[color:var(--brand-navy)]">Produit alimentaire</div>
          <div className="text-[11px] text-[hsl(var(--muted-foreground))]">Affiche les allergènes, la liste d'ingrédients, la DLC et l'origine sur la fiche publique. Obligatoire pour les produits consommables (règlement INCO).</div>
        </div>
      </label>

      {edit.is_food && (
        <>
          <div className="p-4 rounded-xl border bg-white space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <Switch checked={!!edit.price_per_kg} onCheckedChange={v => setEdit({ ...edit, price_per_kg: v })} data-testid="product-price-per-kg" />
              <div>
                <div className="text-sm font-bold text-[color:var(--brand-navy)]">Prix au kilo</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))]">Le prix affiché est un <strong>prix au kg</strong>. Utile pour les charcuteries, fromages, fruits & légumes pesés.</div>
              </div>
            </label>
            {edit.price_per_kg && (
              <div className="pt-3 border-t grid md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold">Poids moyen par unité (g)</Label>
                  <Input
                    type="number"
                    value={edit.unit_weight_g || ""}
                    onChange={e => setEdit({ ...edit, unit_weight_g: parseInt(e.target.value) || null })}
                    placeholder="Ex: 250 pour une portion de 250g"
                    className="mt-1.5 text-sm"
                    data-testid="product-unit-weight"
                  />
                  <div className="text-[11px] text-slate-500 mt-1">Sert à estimer le prix (prix_kg × poids/1000). Le poids réel est saisi à la préparation.</div>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 rounded-xl border bg-white">
            <Label className="text-xs font-bold">Allergènes déclarés (obligatoire INCO UE)</Label>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-1.5" data-testid="product-allergens">
              {allergensRef.map(a => {
                const active = (edit.allergens || []).includes(a.code);
                return (
                  <button
                    key={a.code}
                    type="button"
                    onClick={() => toggleAllergen(a.code)}
                    className={`px-2.5 py-2 rounded-lg text-xs font-semibold border-2 transition text-left flex items-center gap-2 ${active ? "border-amber-500 bg-amber-50 text-amber-900" : "border-slate-200 hover:border-slate-300 bg-white text-slate-700"}`}
                    data-testid={`allergen-${a.code}`}
                  >
                    <span className="text-base leading-none">{a.icon}</span> {a.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-4 rounded-xl border bg-white space-y-3">
            <div>
              <Label className="text-xs font-bold">Liste d'ingrédients</Label>
              <Textarea
                rows={4}
                value={edit.ingredients || ""}
                onChange={e => setEdit({ ...edit, ingredients: e.target.value })}
                placeholder="Ex: Farine de blé, eau, levure, sel. Traces possibles de gluten et œuf."
                className="mt-1.5 text-sm"
                data-testid="product-ingredients"
              />
              <div className="text-[11px] text-slate-500 mt-1">Les allergènes seront automatiquement mis en gras sur la fiche publique.</div>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-bold">DLC (jours)</Label>
                <Input type="number" value={edit.dlc_days || ""} onChange={e => setEdit({ ...edit, dlc_days: parseInt(e.target.value) || null })} placeholder="Ex: 5" className="mt-1.5 text-sm" data-testid="product-dlc" />
              </div>
              <div>
                <Label className="text-xs font-bold">Origine</Label>
                <Input value={edit.origin || ""} onChange={e => setEdit({ ...edit, origin: e.target.value })} placeholder="France, Bouches-du-Rhône…" className="mt-1.5 text-sm" />
              </div>
              <div>
                <Label className="text-xs font-bold">Conservation</Label>
                <Input value={edit.conservation || ""} onChange={e => setEdit({ ...edit, conservation: e.target.value })} placeholder="À conserver entre 0 et 4°C" className="mt-1.5 text-sm" />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}



// =============== RentalEditor (Phase 16) ===============
function RentalEditor({ edit, setEdit }) {
  const units = edit.rental_units || [];

  const addUnit = () => {
    const id = (crypto.randomUUID && crypto.randomUUID()) || Math.random().toString(36).slice(2, 10);
    setEdit({ ...edit, rental_units: [...units, { id, name: `Unité #${units.length + 1}`, notes: "", image: "", is_active: true }] });
  };
  const updateUnit = (i, patch) => {
    const next = units.map((u, idx) => idx === i ? { ...u, ...patch } : u);
    setEdit({ ...edit, rental_units: next });
  };
  const removeUnit = (i) => {
    if (!window.confirm("Supprimer cette unité ? Les réservations existantes ne seront pas effacées.")) return;
    setEdit({ ...edit, rental_units: units.filter((_, idx) => idx !== i) });
  };

  return (
    <div className="space-y-5" data-testid="rental-editor">
      <div className="flex items-center justify-between p-4 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
        <div>
          <Label className="text-sm font-semibold">Activer la location pour ce produit</Label>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Active le calendrier par unité, le picker de dates, et le checkout dédié.</p>
        </div>
        <Switch checked={!!edit.is_rental} onCheckedChange={(v) => setEdit({ ...edit, is_rental: v })} data-testid="product-is-rental"/>
      </div>

      {edit.is_rental && (
        <>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Unité de durée</Label>
              <Select value={edit.rental_unit || "day"} onValueChange={(v) => setEdit({ ...edit, rental_unit: v })}>
                <SelectTrigger data-testid="rental-unit"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hour">À l'heure</SelectItem>
                  <SelectItem value="day">À la journée</SelectItem>
                  <SelectItem value="night">À la nuit (hébergement)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Prix par unité (€)</Label>
              <Input type="number" step="0.01" min="0" value={priceFromCents(edit.rental_price_cents || 0)}
                onChange={(e) => setEdit({ ...edit, rental_price_cents: centsFromPrice(e.target.value) })}
                data-testid="rental-price"/>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Total = prix × durée choisie par le client</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Caution (€) — indicative</Label>
              <Input type="number" step="0.01" min="0" value={priceFromCents(edit.rental_deposit_cents || 0)}
                onChange={(e) => setEdit({ ...edit, rental_deposit_cents: centsFromPrice(e.target.value) })}
                data-testid="rental-caution"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Durée minimum</Label>
              <Input type="number" min="1" value={edit.rental_min_duration || 1}
                onChange={(e) => setEdit({ ...edit, rental_min_duration: parseInt(e.target.value) || 1 })}
                data-testid="rental-min"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Durée maximum</Label>
              <Input type="number" min="1" value={edit.rental_max_duration || 30}
                onChange={(e) => setEdit({ ...edit, rental_max_duration: parseInt(e.target.value) || 30 })}
                data-testid="rental-max"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Tampon entre 2 locations (h)</Label>
              <Input type="number" min="0" value={edit.rental_buffer_hours || 0}
                onChange={(e) => setEdit({ ...edit, rental_buffer_hours: parseInt(e.target.value) || 0 })}
                data-testid="rental-buffer"/>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Nettoyage, vérification…</p>
            </div>

            {edit.rental_unit === "night" && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Heure d'arrivée (check-in)</Label>
                  <Input type="time" value={edit.rental_checkin_time || "15:00"}
                    onChange={(e) => setEdit({ ...edit, rental_checkin_time: e.target.value })}/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Heure de départ (check-out)</Label>
                  <Input type="time" value={edit.rental_checkout_time || "11:00"}
                    onChange={(e) => setEdit({ ...edit, rental_checkout_time: e.target.value })}/>
                </div>
              </>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Description location (conditions, équipements, caution, etc.)</Label>
            <Textarea rows={3} value={edit.rental_description || ""}
              onChange={(e) => setEdit({ ...edit, rental_description: e.target.value })}
              data-testid="rental-description"/>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Unités nommées ({units.length})</Label>
              <Button size="sm" type="button" onClick={addUnit} data-testid="add-rental-unit">
                <Plus className="w-4 h-4 mr-1"/>Ajouter une unité
              </Button>
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
              Créez une unité par objet/chambre physiquement distinct (ex: "Vélo #1", "Vélo #2", "Chambre Rose", "Chambre Bleue").
              Chaque unité a son propre calendrier d'occupation.
            </p>
            {units.length === 0 ? (
              <div className="p-6 text-center border-2 border-dashed border-[hsl(var(--border))] rounded-lg text-sm text-[hsl(var(--muted-foreground))]">
                Aucune unité. Ajoutez-en au moins une pour permettre les réservations.
              </div>
            ) : (
              <div className="space-y-2">
                {units.map((u, i) => (
                  <div key={u.id || i} className="p-3 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))] grid md:grid-cols-[1fr_1fr_auto_auto] gap-2 items-center" data-testid={`rental-unit-${i}`}>
                    <Input value={u.name || ""} placeholder="Nom (ex: Vélo #1)" onChange={(e) => updateUnit(i, { name: e.target.value })}/>
                    <Input value={u.notes || ""} placeholder="Notes (ex: taille M, rouge)" onChange={(e) => updateUnit(i, { notes: e.target.value })}/>
                    <div className="flex items-center gap-2">
                      <Switch checked={u.is_active !== false} onCheckedChange={(v) => updateUnit(i, { is_active: v })}/>
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">{u.is_active !== false ? "Actif" : "Inactif"}</span>
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => removeUnit(i)} data-testid={`rm-unit-${i}`}>
                      <Trash2 className="w-4 h-4 text-red-600"/>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
